import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { CalendarIcon, CloseIcon } from '@assets/icons';
import ResponsiveSearchDrawerItem from '@components/views/hotel/listing/components/ResponsiveSearch/ResponsiveSearchDrawerItem';
import CustomIcon from '@components/utils/CustomIcon';
import ShopAndFlyResponsiveSearchDrawerItem from '@domain/tour/searchBar/searchMonthDate/ResponsiveSearchDrawerItem';

import {
  MobileApplyButton,
  MobileDrawerItemWrapper,
  MobileSearchDrawer,
  MobileSearchDrawerContent,
} from './styled';
import ToggleTabPanel from './components/ToggleTabPanel';
import YearTab from './components/YearTab';
import { MonthItem, YearTabProps } from './interface';

interface Props {
  currentYearTab: YearTabProps;
  setCurrentYearTab: Dispatch<SetStateAction<YearTabProps>>;
  nextYearTab: YearTabProps;
  setNextYearTab: Dispatch<SetStateAction<YearTabProps>>;
  selectedMonthsLabel?: string;
  inputComponent: 'shopAndFly' | 'setur';
}

type InputComponentsType = 'shopAndFly' | 'setur';

const InputComponents = {
  shopAndFly: ShopAndFlyResponsiveSearchDrawerItem,
  setur: ResponsiveSearchDrawerItem,
};

const MobileSearchMonth: FC<Props> = ({
  setCurrentYearTab: setFinalCurrentYearTab,
  setNextYearTab: setFinalNextYearTab,
  currentYearTab: initialCurrentYearTabInfo,
  nextYearTab: initialNextYearTabInfo,
  selectedMonthsLabel,
  inputComponent,
}) => {
  const [visibleSearchModal, setVisibleSearchModal] = useState(false);

  const toggleSearchModal = () => setVisibleSearchModal((val) => !val);
  const [currentYearTab, setCurrentYearTab] = useState<YearTabProps>({
    ...initialCurrentYearTabInfo,
  });
  const [nextYearTab, setNextYearTab] = useState<YearTabProps>({
    ...initialNextYearTabInfo,
  });

  const [selectedYear, setSelectedYear] = useState<'current' | 'next'>(
    'current'
  );

  const toggleYearTab = (val: 'current' | 'next') => setSelectedYear(val);

  const handleMonthChange = (month: string, checked: boolean) => {
    if (selectedYear === 'current') {
      setCurrentYearTab((val) => ({
        ...val,
        months: val.months.map((item) =>
          item.name === month ? { ...item, checked } : item
        ),
      }));
    }

    if (selectedYear === 'next') {
      setNextYearTab((val) => ({
        ...val,
        months: val.months.map((item) =>
          item.name === month ? { ...item, checked } : item
        ),
      }));
    }
  };

  const toggleAllMonths = (checked: boolean) => {
    if (selectedYear === 'current') {
      setCurrentYearTab((val) => ({
        ...val,
        months: val.months.map((item) => ({ ...item, checked })),
      }));
    }

    if (selectedYear === 'next') {
      setNextYearTab((val) => ({
        ...val,
        months: val.months.map((item) => ({ ...item, checked })),
      }));
    }
  };

  const handleAppleClick = () => {
    setFinalCurrentYearTab(currentYearTab);
    setFinalNextYearTab(nextYearTab);
    toggleSearchModal();
  };

  const getSelectedMonthsCount = useCallback((months: MonthItem[]) => {
    return months.filter((month) => month.checked).length;
  }, []);

  useEffect(() => {
    setCurrentYearTab({ ...initialCurrentYearTabInfo });
  }, [initialCurrentYearTabInfo]);

  useEffect(() => {
    setNextYearTab({ ...initialNextYearTabInfo });
  }, [initialNextYearTabInfo]);

  const InputComponent = InputComponents[inputComponent as InputComponentsType];

  return (
    <MobileDrawerItemWrapper inputComponent='shopAndFly'>
      <InputComponent
        title='Ne Zaman Gitmek İstersiniz?'
        subTitle={selectedMonthsLabel || 'Tarih Seçimi'}
        icon={CalendarIcon}
        arrowIcon={true}
        onClick={toggleSearchModal}
        isFilled={!!selectedMonthsLabel}
      />

      <MobileSearchDrawer
        title='Tarih Seçimi'
        open={visibleSearchModal}
        closeIcon={
          <CustomIcon icon={CloseIcon} fontSize={24} paletteColor='black' />
        }
        onClose={toggleSearchModal}
        destroyOnClose
        height='100%'
        width='100%'
      >
        <MobileSearchDrawerContent>
          <ToggleTabPanel
            toggleYearTab={toggleYearTab}
            selectedYear={selectedYear}
            currentYear={currentYearTab.year}
            nextYear={nextYearTab.year}
            currentYearSelectedMonths={getSelectedMonthsCount(
              currentYearTab.months
            )}
            nextYearSelectedMonths={getSelectedMonthsCount(nextYearTab.months)}
          />
          {selectedYear === 'current' ? (
            <YearTab
              handleMonthChange={handleMonthChange}
              months={currentYearTab.months}
              toggleAllMonths={toggleAllMonths}
            />
          ) : null}

          {selectedYear === 'next' ? (
            <YearTab
              handleMonthChange={handleMonthChange}
              months={nextYearTab.months}
              toggleAllMonths={toggleAllMonths}
            />
          ) : null}

          <MobileApplyButton onClick={handleAppleClick}>
            Uygula
          </MobileApplyButton>
        </MobileSearchDrawerContent>
      </MobileSearchDrawer>
    </MobileDrawerItemWrapper>
  );
};

export default MobileSearchMonth;
